import { MastershopSyncStatus } from './gamification/MastershopSyncStatus';
import React, { useState, useEffect } from 'react';
import { 
  Trophy, 
  ShoppingBag, 
  RefreshCw, 
  Sparkles, 
  Search, 
  TrendingUp, 
  CheckCircle2, 
  AlertCircle, 
  Clock, 
  ExternalLink,
  Shield,
  Gift,
  Medal,
  Crown,
  Users,
  DollarSign,
  ArrowRightLeft,
  Save,
  ChevronDown,
  ChevronUp,
  Store,
  Key
} from 'lucide-react';
import { Button } from './ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { useToast } from '../contexts/ToastContext';
import { mastershopHelpers, getCurrentYearMonth, formatMonthName } from '../helpers/mastershopHelpers';

export const AdminMasterShopTab: React.FC = () => {
  const { showToast } = useToast();
  const [selectedMonth, setSelectedMonth] = useState<string>(getCurrentYearMonth());
  const [integrations, setIntegrations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncingAll, setSyncingAll] = useState(false);
  const [syncingUser, setSyncingUser] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedUserIds, setExpandedUserIds] = useState<Record<string, boolean>>({});
  
  // Estados de Cotação de Moeda (COP -> BRL)
  const [copRate, setCopRate] = useState<number>(0.0016);
  const [rateInput, setRateInput] = useState<string>('0.0016');
  const [savingRate, setSavingRate] = useState(false);

  useEffect(() => {
    loadData();
    loadCurrencyRate();
  }, [selectedMonth]);

  const loadCurrencyRate = async () => {
    try {
      const config = await mastershopHelpers.getCurrencyConfig();
      if (config?.cop_to_brl_rate) {
        setCopRate(config.cop_to_brl_rate);
        setRateInput(String(config.cop_to_brl_rate));
      }
    } catch (e) {
      console.error('Erro ao carregar cotação:', e);
    }
  };

  const handleSaveRate = async () => {
    const num = parseFloat(rateInput.replace(',', '.'));
    if (isNaN(num) || num <= 0) {
      showToast({
        type: 'error',
        title: 'Valor Inválido',
        message: 'Insira um valor numérico válido para a cotação.',
      });
      return;
    }

    try {
      setSavingRate(true);
      const res = await mastershopHelpers.saveCurrencyConfig(num);
      if (res.success) {
        setCopRate(num);
        showToast({
          type: 'success',
          title: 'Cotação Salva!',
          message: `Nova taxa configurada: 1 COP = R$ ${num} (R$ ${(num * 100).toFixed(2)} a cada 100 COP).`,
        });
      } else {
        showToast({
          type: 'error',
          title: 'Erro ao Salvar',
          message: res.error || 'Falha ao atualizar cotação.',
        });
      }
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Erro',
        message: err.message || 'Erro inesperado ao salvar taxa.',
      });
    } finally {
      setSavingRate(false);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const { data, error } = await mastershopHelpers.getAdminIntegrationsList(selectedMonth);
      if (!error && data) {
        setIntegrations(data);
      }
    } catch (err) {
      console.error('Erro ao carregar dados do admin Master Shop:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSyncAll = async () => {
    try {
      setSyncingAll(true);
      const res = await mastershopHelpers.syncAllSellers(selectedMonth);
      if (res.success) {
        showToast({
          type: 'success',
          title: 'Sincronização solicitada',
          message: res.data?.queued === false ? 'Já houve uma solicitação recente. Aguarde cinco minutos para solicitar outra.' : 'Processamento em segundo plano. O total será atualizado ao concluir todas as lojas.',
        });
        await loadData();
      } else {
        showToast({
          type: 'error',
          title: 'Erro na Sincronização',
          message: res.error || 'Falha ao sincronizar vendedores.',
        });
      }
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Erro',
        message: err.message || 'Erro inesperado.',
      });
    } finally {
      setSyncingAll(false);
    }
  };

  const handleSyncSingle = async (userId: string, sellerName: string) => {
    try {
      setSyncingUser(userId);
      const res = await mastershopHelpers.syncRevenue(userId, selectedMonth);
      if (res.success) {
        showToast({
          type: 'success',
          title: 'Sincronização solicitada',
          message: res.data?.queued === false ? 'Já houve uma solicitação recente. Aguarde cinco minutos para solicitar outra.' : 'Processamento em segundo plano. O total será atualizado ao concluir todas as lojas.',
        });
        await loadData();
      } else {
        showToast({
          type: 'error',
          title: 'Falha ao Sincronizar',
          message: res.error || 'Verifique a chave de API das lojas.',
        });
      }
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Erro',
        message: err.message || 'Erro ao sincronizar.',
      });
    } finally {
      setSyncingUser(null);
    }
  };

  const toggleExpand = (userId: string) => {
    setExpandedUserIds(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  const formatBrl = (val: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      maximumFractionDigits: 2,
    }).format(val || 0);
  };

  const formatCop = (val: number) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      maximumFractionDigits: 0,
    }).format(val || 0);
  };

  const maskApiKey = (key: string) => {
    if (!key) return '••••••••';
    if (key.length <= 8) return '••••••••';
    return `${key.slice(0, 7)}...${key.slice(-4)}`;
  };

  const getInitials = (name: string) => {
    return (name || 'Vendedor')
      .split(' ')
      .map(n => n[0])
      .join('')
      .slice(0, 2)
      .toUpperCase();
  };

  // Métricas calculadas
  const totalMonthlyRevenueBrl = integrations.reduce((acc, item) => acc + Number(item.revenue?.total_revenue || 0), 0);
  const totalMonthlyRevenueCop = integrations.reduce((acc, item) => acc + Number(item.revenue?.revenue_cop || 0), 0);
  const totalMonthlyOrders = integrations.reduce((acc, item) => acc + Number(item.revenue?.total_orders || 0), 0);
  const totalConnectedStores = integrations.reduce((acc, item) => acc + Number(item.stores?.length || 0), 0);
  const club100kCount = integrations.filter(item => Number(item.revenue?.total_revenue || 0) >= 100000).length;

  // Ordenação por faturamento consolidado para o ranking
  const sortedByRevenue = [...integrations].sort((a, b) => {
    const revA = Number(a.revenue?.total_revenue || 0);
    const revB = Number(b.revenue?.total_revenue || 0);
    return revB - revA;
  });

  const filteredItems = sortedByRevenue.filter(item => {
    const name = (item.user?.full_name || '').toLowerCase();
    const email = (item.user?.email || '').toLowerCase();
    const q = searchQuery.toLowerCase();
    return name.includes(q) || email.includes(q);
  });

  const simulatedCop = 10000000;
  const parsedRate = parseFloat(rateInput.replace(',', '.')) || copRate || 0.0016;
  const simulatedBrl = simulatedCop * parsedRate;

  return (
    <div className="space-y-8">
      <MastershopSyncStatus month={selectedMonth} onUpdate={loadData} />
      {/* Header & Controles */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-[#1a1d21] p-6 rounded-3xl border border-gray-800 shadow-xl">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-[#0FFF55]/20 border border-[#0FFF55]/30 rounded-2xl">
              <Trophy className="w-6 h-6 text-[#0FFF55]" />
            </div>
            <div>
              <h2 className="text-2xl font-black text-white tracking-tight">
                Faturamento Master Shop & Top Sellers
              </h2>
              <p className="text-gray-400 text-xs sm:text-sm">
                Monitore o faturamento consolidado e o detalhamento de cada loja dos vendedores.
              </p>
            </div>
          </div>
        </div>

        {/* Seletor de Mês e Ação Global */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1.5 bg-[#121316] p-1 rounded-xl border border-gray-700">
            <button
              onClick={() => setSelectedMonth('2026-09')}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                selectedMonth === '2026-09'
                  ? 'bg-[#0FFF55] text-[#121316] shadow-sm'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Setembro (Atual)
            </button>
            <button
              onClick={() => setSelectedMonth('2026-10')}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                selectedMonth === '2026-10'
                  ? 'bg-[#0FFF55] text-[#121316] shadow-sm'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Outubro (Competição)
            </button>
          </div>

          <Button
            onClick={handleSyncAll}
            disabled={syncingAll || loading}
            className="bg-gradient-to-r from-[#0FFF55] to-[#0dd149] hover:from-[#0dd149] hover:to-[#0FFF55] text-[#121316] font-black rounded-xl text-xs flex items-center gap-2 shadow-lg shadow-[#0FFF55]/20 transition-transform active:scale-95"
          >
            <RefreshCw className={`w-4 h-4 ${syncingAll ? 'animate-spin' : ''}`} />
            {syncingAll ? 'Sincronizando Lojas...' : 'Sincronizar Todas as Lojas'}
          </Button>
        </div>
      </div>

      {/* Card de Configuração de Cotação e Câmbio (COP -> BRL) */}
      <div className="bg-gradient-to-br from-[#1c1f26] via-[#16181f] to-[#121418] border border-[#0FFF55]/20 rounded-3xl p-6 shadow-xl">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
          <div className="space-y-1.5 max-w-xl">
            <div className="flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5 text-[#0FFF55]" />
              <h3 className="font-extrabold text-lg text-white">
                Cotação de Câmbio • Peso Colombiano (COP) para Real (BRL)
              </h3>
            </div>
            <p className="text-gray-400 text-xs sm:text-sm leading-relaxed">
              A Master Shop opera em <strong className="text-gray-200">Pesos Colombianos (COP)</strong>. O sistema utiliza este multiplicador para converter o faturamento em <strong className="text-[#0FFF55]">Reais (BRL)</strong> para o cálculo das metas e ranking.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-4 bg-[#121316] p-4 rounded-2xl border border-gray-700/80">
            <div>
              <label className="text-gray-400 text-[11px] font-semibold uppercase block mb-1">
                Multiplicador (1 COP em BRL)
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={rateInput}
                  onChange={(e) => setRateInput(e.target.value)}
                  placeholder="0.0016"
                  className="w-32 bg-[#1a1d21] text-[#0FFF55] font-black px-3 py-2 rounded-xl text-sm border border-gray-600 focus:outline-none focus:border-[#0FFF55]"
                />
                <Button
                  onClick={handleSaveRate}
                  disabled={savingRate}
                  size="sm"
                  className="bg-[#0FFF55] hover:bg-[#0dd149] text-[#121316] font-bold rounded-xl text-xs flex items-center gap-1.5 shadow-md shadow-[#0FFF55]/20"
                >
                  <Save className="w-3.5 h-3.5" />
                  {savingRate ? 'Salvando...' : 'Salvar Taxa'}
                </Button>
              </div>
            </div>

            <div className="border-l border-gray-700 pl-4 space-y-0.5">
              <span className="text-[11px] text-gray-400 font-semibold block">Simulação em Tempo Real</span>
              <span className="text-xs text-gray-300 block">
                10.000.000 COP = <strong className="text-[#0FFF55] font-black">{formatBrl(simulatedBrl)}</strong>
              </span>
              <span className="text-[10px] text-gray-500 block">
                (R$ {(parsedRate * 100).toFixed(2)} por 100 COP)
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-[#1a1d21] rounded-3xl p-6 border border-gray-800 relative overflow-hidden shadow-xl">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-xs font-semibold uppercase tracking-wider">Faturamento Consolidado</p>
              <p className="text-2xl sm:text-3xl font-black text-[#0FFF55] mt-1">
                {formatBrl(totalMonthlyRevenueBrl)}
              </p>
              <span className="text-[11px] text-gray-500 font-medium">
                {totalMonthlyRevenueCop > 0 ? formatCop(totalMonthlyRevenueCop) : `Em ${formatMonthName(selectedMonth)}`}
              </span>
            </div>
            <div className="p-3 bg-[#0FFF55]/10 rounded-2xl border border-[#0FFF55]/20">
              <TrendingUp className="w-6 h-6 text-[#0FFF55]" />
            </div>
          </div>
        </div>

        <div className="bg-[#1a1d21] rounded-3xl p-6 border border-gray-800 relative overflow-hidden shadow-xl">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-xs font-semibold uppercase tracking-wider">Pedidos Totais</p>
              <p className="text-2xl sm:text-3xl font-black text-white mt-1">
                {totalMonthlyOrders}
              </p>
              <span className="text-[11px] text-gray-500 font-medium">Processados na Master Shop</span>
            </div>
            <div className="p-3 bg-blue-500/10 rounded-2xl border border-blue-500/20">
              <ShoppingBag className="w-6 h-6 text-blue-400" />
            </div>
          </div>
        </div>

        <div className="bg-[#1a1d21] rounded-3xl p-6 border border-gray-800 relative overflow-hidden shadow-xl">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-xs font-semibold uppercase tracking-wider">Lojas Conectadas</p>
              <p className="text-2xl sm:text-3xl font-black text-white mt-1">
                {totalConnectedStores}
              </p>
              <span className="text-[11px] text-gray-500 font-medium">Em {integrations.length} vendedores</span>
            </div>
            <div className="p-3 bg-purple-500/10 rounded-2xl border border-purple-500/20">
              <Users className="w-6 h-6 text-purple-400" />
            </div>
          </div>
        </div>

        <div className="bg-[#1a1d21] rounded-3xl p-6 border border-gray-800 relative overflow-hidden shadow-xl">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-xs font-semibold uppercase tracking-wider">Clube 100k+ (BRL)</p>
              <p className="text-2xl sm:text-3xl font-black text-yellow-400 mt-1">
                {club100kCount}
              </p>
              <span className="text-[11px] text-gray-500 font-medium">Bateram a 1ª grande meta</span>
            </div>
            <div className="p-3 bg-yellow-500/10 rounded-2xl border border-yellow-500/20">
              <Crown className="w-6 h-6 text-yellow-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Tabela de Vendedores e Ranking */}
      <div className="bg-[#1a1d21] rounded-3xl border border-gray-800 shadow-2xl overflow-hidden">
        {/* Barra de Busca */}
        <div className="p-6 border-b border-gray-800 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="relative w-full sm:w-80">
            <Search className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Buscar por nome ou email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-[#121316] text-white pl-10 pr-4 py-2 rounded-xl text-sm border border-gray-700 focus:outline-none focus:border-[#0FFF55]"
            />
          </div>

          <span className="text-xs text-gray-400 font-medium">
            Mostrando {filteredItems.length} vendedores ({totalConnectedStores} lojas no total)
          </span>
        </div>

        {loading ? (
          <div className="py-20 text-center space-y-3">
            <RefreshCw className="w-8 h-8 text-[#0FFF55] animate-spin mx-auto" />
            <p className="text-gray-400 text-sm">Carregando dados das lojas da Master Shop...</p>
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="py-16 text-center space-y-3">
            <Trophy className="w-12 h-12 text-gray-600 mx-auto" />
            <h4 className="text-lg font-bold text-white">Nenhum vendedor encontrado</h4>
            <p className="text-gray-400 text-xs max-w-sm mx-auto">
              Quando os membros cadastrarem suas chaves de API da Master Shop no perfil, eles aparecerão automaticamente aqui.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-gray-800 text-[11px] font-bold text-gray-400 uppercase tracking-wider bg-[#16181d]">
                  <th className="py-4 px-6">Posição</th>
                  <th className="py-4 px-6">Vendedor</th>
                  <th className="py-4 px-6">Lojas</th>
                  <th className="py-4 px-6">Faturamento Consolidado</th>
                  <th className="py-4 px-6">Pedidos</th>
                  <th className="py-4 px-6">Meta Atual (BRL)</th>
                  <th className="py-4 px-6">Status Geral</th>
                  <th className="py-4 px-6 text-right">Ação</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800/60 text-sm">
                {filteredItems.map((item, idx) => {
                  const rev = item.revenue;
                  const totalBrl = Number(rev?.total_revenue || 0);
                  const totalCop = Number(rev?.revenue_cop || 0);
                  const orders = Number(rev?.total_orders || 0);
                  const rank = idx + 1;
                  const isExpanded = !!expandedUserIds[item.user_id];
                  const userStores = item.stores || [];

                  return (
                    <React.Fragment key={item.user_id}>
                      <tr className="hover:bg-gray-800/30 transition-colors">
                        {/* Posição */}
                        <td className="py-4 px-6">
                          <div className="flex items-center gap-1.5 font-black text-sm">
                            {rank === 1 && <Crown className="w-4 h-4 text-yellow-400" />}
                            {rank === 2 && <Medal className="w-4 h-4 text-slate-300" />}
                            {rank === 3 && <Medal className="w-4 h-4 text-amber-500" />}
                            <span className={rank === 1 ? 'text-yellow-400' : rank === 2 ? 'text-slate-300' : rank === 3 ? 'text-amber-500' : 'text-gray-500'}>
                              #{rank}
                            </span>
                          </div>
                        </td>

                        {/* Vendedor */}
                        <td className="py-4 px-6">
                          <div className="flex items-center gap-3">
                            <Avatar className="w-10 h-10 ring-2 ring-gray-700">
                              <AvatarImage src={item.user?.avatar_url} />
                              <AvatarFallback className="bg-gray-800 text-gray-200 font-bold text-xs">
                                {getInitials(item.user?.full_name)}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-bold text-white text-sm">
                                  {item.user?.full_name || 'Vendedor'}
                                </span>
                                {item.user?.role === 'admin' && (
                                  <span className="bg-[#0FFF55]/10 text-[#0FFF55] text-[10px] font-black px-1.5 py-0.5 rounded border border-[#0FFF55]/30">
                                    ADMIN
                                  </span>
                                )}
                              </div>
                              <span className="text-xs text-gray-400 block">{item.user?.email}</span>
                            </div>
                          </div>
                        </td>

                        {/* Quantidade de Lojas & Botão Expandir */}
                        <td className="py-4 px-6">
                          <button
                            onClick={() => toggleExpand(item.user_id)}
                            className="inline-flex items-center gap-1.5 bg-[#14161b] hover:bg-gray-800 text-xs font-bold text-gray-300 hover:text-white px-2.5 py-1 rounded-xl border border-gray-700 transition-colors"
                          >
                            <Store className="w-3.5 h-3.5 text-[#0FFF55]" />
                            <span>{userStores.length} {userStores.length === 1 ? 'Loja' : 'Lojas'}</span>
                            {isExpanded ? (
                              <ChevronUp className="w-3.5 h-3.5 text-gray-400" />
                            ) : (
                              <ChevronDown className="w-3.5 h-3.5 text-gray-400" />
                            )}
                          </button>
                        </td>

                        {/* Faturamento Consolidado */}
                        <td className="py-4 px-6">
                          <span className="text-base font-black text-[#0FFF55] block">
                            {formatBrl(totalBrl)}
                          </span>
                          {totalCop > 0 && (
                            <span className="text-[11px] text-gray-400 font-medium block">
                              {formatCop(totalCop)}
                            </span>
                          )}
                        </td>

                        {/* Pedidos */}
                        <td className="py-4 px-6 text-gray-300 font-semibold">
                          {orders} pedidos
                        </td>

                        {/* Meta Atual & Conquistas */}
                        <td className="py-4 px-6">
                          <span className="text-xs font-bold text-gray-300 block">
                            {formatBrl(rev?.current_milestone_target || 100000)}
                          </span>
                          {rev?.claimed_milestones?.length > 0 && (
                            <div className="flex items-center gap-1 mt-1">
                              {rev.claimed_milestones.map((m: string) => (
                                <span key={m} className="bg-yellow-400/20 text-yellow-300 text-[10px] font-black px-1.5 py-0.2 rounded border border-yellow-400/30">
                                  {m.toUpperCase()} 🏆
                                </span>
                              ))}
                            </div>
                          )}
                        </td>

                        {/* Status Geral da Conexão */}
                        <td className="py-4 px-6">
                          <div className="space-y-0.5">
                            <div className="flex items-center gap-1.5">
                              {item.last_sync_status === 'success' ? (
                                <span className="flex items-center gap-1 text-[11px] font-bold text-green-400">
                                  <CheckCircle2 className="w-3.5 h-3.5" /> Sincronizado
                                </span>
                              ) : item.last_sync_status === 'error' ? (
                                <span className="flex items-center gap-1 text-[11px] font-bold text-red-400">
                                  <AlertCircle className="w-3.5 h-3.5" /> Erro em loja
                                </span>
                              ) : (
                                <span className="flex items-center gap-1 text-[11px] font-bold text-yellow-400">
                                  <Clock className="w-3.5 h-3.5" /> Pendente
                                </span>
                              )}
                            </div>
                            {item.last_sync_at && (
                              <span className="text-[10px] text-gray-500 block">
                                {new Date(item.last_sync_at).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}
                              </span>
                            )}
                          </div>
                        </td>

                        {/* Ação */}
                        <td className="py-4 px-6 text-right">
                          <Button
                            onClick={() => handleSyncSingle(item.user_id, item.user?.full_name || 'Vendedor')}
                            disabled={syncingUser === item.user_id}
                            variant="outline"
                            size="sm"
                            className="border-gray-700 text-gray-300 hover:text-white hover:bg-gray-800 rounded-xl text-xs flex items-center gap-1.5 ml-auto"
                          >
                            <RefreshCw className={`w-3 h-3 ${syncingUser === item.user_id ? 'animate-spin text-[#0FFF55]' : ''}`} />
                            {syncingUser === item.user_id ? 'Sincronizando...' : 'Sincronizar'}
                          </Button>
                        </td>
                      </tr>

                      {/* Accordion com Detalhamento Individual de Lojas */}
                      {isExpanded && (
                        <tr className="bg-[#121418] border-y border-gray-800/80">
                          <td colSpan={8} className="p-4 sm:p-6">
                            <div className="bg-[#181a20] rounded-2xl border border-gray-700/80 p-4 space-y-3">
                              <div className="flex items-center justify-between pb-2 border-b border-gray-800">
                                <span className="text-xs font-bold text-white flex items-center gap-2">
                                  <Store className="w-4 h-4 text-[#0FFF55]" />
                                  Detalhamento Individual das Lojas ({userStores.length})
                                </span>
                                <span className="text-[11px] text-gray-400 font-mono">
                                  ID Vendedor: {item.user_id}
                                </span>
                              </div>

                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                {userStores.map((s: any, sIdx: number) => {
                                  const sRevenueBrl = Number(s.last_sync_revenue_brl || 0);
                                  const sRevenueCop = Number(s.last_sync_revenue_cop || 0);
                                  const sOrders = Number(s.last_sync_orders || 0);
                                  const isStoreSuccess = s.last_sync_status === 'success';
                                  const isStoreError = s.last_sync_status === 'error';

                                  return (
                                    <div 
                                      key={s.id || sIdx}
                                      className="bg-[#121316] rounded-xl p-3 border border-gray-800 space-y-2"
                                    >
                                      <div className="flex items-center justify-between">
                                        <h5 className="font-bold text-white text-xs truncate">
                                          {s.store_name || `Loja ${sIdx + 1}`}
                                        </h5>
                                        {isStoreSuccess ? (
                                          <span className="text-[10px] text-green-400 font-bold flex items-center gap-1">
                                            <CheckCircle2 className="w-3 h-3" /> OK
                                          </span>
                                        ) : isStoreError ? (
                                          <span className="text-[10px] text-red-400 font-bold flex items-center gap-1" title={s.last_error_message}>
                                            <AlertCircle className="w-3 h-3" /> Erro
                                          </span>
                                        ) : (
                                          <span className="text-[10px] text-yellow-400 font-bold">Pendente</span>
                                        )}
                                      </div>

                                      <div className="grid grid-cols-2 gap-2 text-[11px] bg-[#1a1d22] p-2 rounded-lg">
                                        <div>
                                          <span className="text-gray-400 block text-[10px]">Faturamento</span>
                                          <span className="font-black text-[#0FFF55]">{formatBrl(sRevenueBrl)}</span>
                                          {sRevenueCop > 0 && (
                                            <span className="text-gray-500 block text-[9px]">{formatCop(sRevenueCop)}</span>
                                          )}
                                        </div>
                                        <div>
                                          <span className="text-gray-400 block text-[10px]">Pedidos</span>
                                          <span className="font-bold text-white">{sOrders}</span>
                                        </div>
                                      </div>

                                      <div className="flex items-center justify-between text-[10px] text-gray-500 font-mono pt-1">
                                        <span className="flex items-center gap-1">
                                          <Key className="w-2.5 h-2.5" /> {maskApiKey(s.api_key)}
                                        </span>
                                        <span>
                                          {s.last_sync_at ? new Date(s.last_sync_at).toLocaleDateString('pt-BR') : 'Nunca'}
                                        </span>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
